import { useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Loader2, Send, Eye, FileText, AlertCircle, Check } from "lucide-react";
import { useAuth, getAuthToken } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:9000/";
const S3_BASE_URL = process.env.REACT_APP_S3_BASE_URL;

const getDisplayUrl = (id) => {
  if (!id) return null;
  if (
    typeof id === "string" &&
    (id.startsWith("http") ||
      id.startsWith("data:image/") ||
      id.startsWith("blob:"))
  ) {
    return id;
  }
  if (typeof id === "string" && id.startsWith("images/")) {
    return `${S3_BASE_URL}/${id}`;
  }
  return null;
};

const ComposeEmailDialog = ({ open, onOpenChange }) => {
  const { company } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Build available sender options
  const senderOptions = [];
  if (company?.email_settings?.sender_email) {
    senderOptions.push(company.email_settings.sender_email);
  }
  if (
    company?.email_settings?.smtp_user &&
    company?.email_settings?.smtp_user !== company?.email_settings?.sender_email
  ) {
    senderOptions.push(company.email_settings.smtp_user);
  }
  // Fallback if no options
  if (senderOptions.length === 0) {
    senderOptions.push("noreply@zichtbaarstudio.be");
  }

  const [formData, setFormData] = useState({
    to: "",
    subject: "",
    body: "",
    from: senderOptions[0] || "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBodyChange = (value) => {
    setFormData((prev) => ({ ...prev, body: value }));
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const token = getAuthToken();
      await axios.post(
        `${API_BASE_URL}api/mails/send`,
        {
          to: formData.to,
          subject: formData.subject,
          body: formData.body,
          from: formData.from,
          company_id: company.id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setSuccess(true);
      setFormData({
        to: "",
        subject: "",
        body: "",
        from: senderOptions[0] || "",
      });
      setTimeout(() => {
        setSuccess(false);
        onOpenChange(false);
      }, 2000);
    } catch (err) {
      console.error("Error sending email:", err);
      setError(
        err.response?.data?.details ||
          err.response?.data?.error ||
          "Kon e-mail niet verzenden.",
      );
    } finally {
      setLoading(false);
    }
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      ["link", "image"],
      ["clean"],
    ],
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`sm:max-w-[700px] rounded-xl sm:h-auto overflow-y-auto p-4 sm:p-6 ${isDark ? "bg-[#141414] border-white/[0.08]" : "bg-white border-gray-200"}`}
      >
        <DialogHeader>
          <DialogTitle
            className={`flex items-center gap-2 text-[15px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
          >
            <Send
              className={`h-4 w-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}
            />
            Nieuw Bericht
          </DialogTitle>
          <DialogDescription
            className={`text-[13px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
          >
            Stuur een e-mail naar een klant of contactpersoon met je eigen
            huisstijl.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-10 flex flex-col items-center justify-center text-center">
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
            >
              <Check
                className={`h-5 w-5 ${isDark ? "text-[#BFFF00]" : "text-gray-900"}`}
              />
            </div>
            <h3
              className={`text-[15px] font-semibold mb-1 ${isDark ? "text-white" : "text-gray-900"}`}
            >
              Verzonden!
            </h3>
            <p
              className={`text-[13px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
            >
              Je e-mail is succesvol verstuurd.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {error && (
              <div
                className={`flex items-center gap-2.5 px-4 py-3 rounded-lg text-[13px] ${isDark ? "bg-red-500/8 border border-red-500/15 text-red-300" : "bg-red-50 border border-red-100 text-red-700"}`}
              >
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Tabs defaultValue="edit" className="w-full">
              <TabsList
                className={`grid w-full grid-cols-2 mb-4 p-0.5 rounded-lg ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
              >
                <TabsTrigger
                  value="edit"
                  className={`flex items-center gap-1.5 rounded-md text-[12px] font-medium data-[state=active]:shadow-sm ${isDark ? "data-[state=active]:bg-white/[0.1] data-[state=active]:text-white text-gray-500" : "data-[state=active]:bg-white data-[state=active]:text-gray-900 text-gray-500"}`}
                >
                  <FileText className="h-3.5 w-3.5" />
                  Bericht
                </TabsTrigger>
                <TabsTrigger
                  value="preview"
                  className={`flex items-center gap-1.5 rounded-md text-[12px] font-medium data-[state=active]:shadow-sm ${isDark ? "data-[state=active]:bg-white/[0.1] data-[state=active]:text-white text-gray-500" : "data-[state=active]:bg-white data-[state=active]:text-gray-900 text-gray-500"}`}
                >
                  <Eye className="h-3.5 w-3.5" />
                  Voorbeeld
                </TabsTrigger>
              </TabsList>

              <TabsContent value="edit" className="space-y-4">
                {/* Sender email selector */}
                <div
                  className={`flex flex-wrap items-center gap-2 sm:gap-3 px-3 py-2.5 rounded-lg ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-gray-50 border border-gray-100"}`}
                >
                  <Label
                    className={`text-[12px] shrink-0 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                  >
                    Van:
                  </Label>
                  {senderOptions.length > 1 ? (
                    <select
                      name="from"
                      value={formData.from}
                      onChange={handleChange}
                      className={`flex-1 min-w-0 text-[12px] font-medium rounded-md px-2.5 py-1 outline-none ${isDark ? "bg-white/[0.04] border border-white/[0.06] text-gray-300" : "bg-white border border-gray-200 text-gray-700"}`}
                    >
                      {senderOptions.map((email) => (
                        <option key={email} value={email}>
                          {email}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span
                      className={`text-[12px] font-medium truncate min-w-0 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                    >
                      {senderOptions[0]}
                    </span>
                  )}
                  {company?.email_settings?.company_name && (
                    <span
                      className={`text-[11px] hidden sm:inline ${isDark ? "text-gray-600" : "text-gray-400"}`}
                    >
                      ({company.email_settings.company_name})
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label
                      className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Aan
                    </Label>
                    <Input
                      id="to"
                      name="to"
                      type="email"
                      placeholder="klant@voorbeeld.be"
                      value={formData.to}
                      onChange={handleChange}
                      required
                      className={`h-9 rounded-lg text-[13px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:border-white/15" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label
                      className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Onderwerp
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      placeholder="Betreft..."
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className={`h-9 rounded-lg text-[13px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:border-white/15" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label
                    className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Bericht
                  </Label>
                  <div
                    className={`compose-quill min-h-[180px] sm:min-h-[300px] mb-8 rounded-lg overflow-hidden border ${isDark ? "border-white/[0.06] compose-quill-dark" : "border-gray-200"}`}
                  >
                    <ReactQuill
                      theme="snow"
                      value={formData.body}
                      onChange={handleBodyChange}
                      modules={modules}
                      className="h-[150px] sm:h-[250px]"
                    />
                  </div>
                  <p
                    className={`text-[11px] text-right ${isDark ? "text-gray-600" : "text-gray-400"}`}
                  >
                    Je huisstijl wordt automatisch toegevoegd.
                  </p>
                </div>
              </TabsContent>

              <TabsContent
                value="preview"
                className={`p-2 sm:p-4 rounded-lg overflow-auto max-h-[50vh] min-h-[200px] sm:min-h-[300px] ${isDark ? "bg-white/[0.03]" : "bg-gray-50"}`}
              >
                <div className="bg-white max-w-2xl mx-auto shadow-sm rounded-lg overflow-hidden border border-gray-100">
                  {/* Header with Logo */}
                  <div className="border-b border-gray-100 p-4 sm:p-6 text-center">
                    {getDisplayUrl(company?.email_settings?.logo_url) ? (
                      <img
                        src={getDisplayUrl(company?.email_settings?.logo_url)}
                        alt="Logo"
                        className="h-12 mx-auto object-contain"
                      />
                    ) : (
                      <div
                        className="text-xl font-bold tracking-tight"
                        style={{
                          color:
                            company?.email_settings?.primary_color || "#1E4FCF",
                        }}
                      >
                        {company?.email_settings?.company_name ||
                          company?.name ||
                          "LOGO"}
                      </div>
                    )}
                  </div>

                  {/* Body */}
                  <div className="p-4 sm:p-8">
                    <h2 className="text-xl font-bold text-gray-900 mb-6">
                      {formData.subject || "(Geen onderwerp)"}
                    </h2>
                    <div
                      className="prose prose-sm max-w-none text-gray-700 min-h-[100px]"
                      dangerouslySetInnerHTML={{
                        __html:
                          formData.body ||
                          "<p style='color:#9ca3af;font-style:italic'>Nog geen bericht getypt...</p>",
                      }}
                    />

                    {/* Signature */}
                    <div className="mt-8 pt-6 border-t border-gray-100 text-sm text-gray-600">
                      {company?.email_settings?.email_signature ? (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: company.email_settings.email_signature,
                          }}
                        />
                      ) : (
                        <>
                          <p className="mb-1">Met vriendelijke groet,</p>
                          <p className="font-semibold">
                            {company?.email_settings?.company_name ||
                              company?.name}
                          </p>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="bg-gray-50 p-4 sm:p-6 text-center border-t border-gray-100">
                    {company?.email_settings?.website_url && (
                      <a
                        href={company.email_settings.website_url}
                        className="text-sm font-medium block mb-2"
                        style={{
                          color:
                            company.email_settings.primary_color || "#1E4FCF",
                        }}
                      >
                        {company.email_settings.website_url.replace(
                          /^https?:\/\//,
                          "",
                        )}
                      </a>
                    )}
                    <p className="text-[10px] text-gray-400">
                      © {new Date().getFullYear()}{" "}
                      {company?.email_settings?.company_name || company?.name}
                    </p>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter
              className={`pt-4 border-t gap-2 ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
            >
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onOpenChange(false)}
                className={`rounded-lg text-[12px] ${isDark ? "border-white/[0.08] text-gray-300 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              >
                Annuleren
              </Button>
              <Button
                size="sm"
                onClick={handleSend}
                disabled={loading || !formData.to || !formData.subject}
                className={`rounded-lg text-[12px] font-medium ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    Verzenden...
                  </>
                ) : (
                  <>
                    <Send className="mr-1.5 h-3.5 w-3.5" />
                    Verzenden
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>

      <style jsx="true">{`
        .compose-quill .ql-toolbar.ql-snow {
          border: none !important;
          border-bottom: 1px solid
            ${isDark ? "rgba(255,255,255,0.06)" : "#e5e7eb"} !important;
          background: ${isDark ? "rgba(255,255,255,0.03)" : "#f9fafb"};
          padding: 8px 12px !important;
        }
        .compose-quill .ql-toolbar .ql-stroke {
          stroke: ${isDark ? "#9ca3af" : "#374151"} !important;
        }
        .compose-quill .ql-toolbar .ql-fill {
          fill: ${isDark ? "#9ca3af" : "#374151"} !important;
        }
        .compose-quill .ql-toolbar .ql-picker-label {
          color: ${isDark ? "#9ca3af" : "#374151"} !important;
        }
        .compose-quill .ql-container.ql-snow {
          border: none !important;
          min-height: 150px;
          font-family:
            -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            "Helvetica Neue", Arial, sans-serif !important;
        }
        .compose-quill .ql-editor {
          padding: 16px !important;
          font-size: 14px;
          line-height: 1.6;
          font-family:
            -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            "Helvetica Neue", Arial, sans-serif !important;
          color: ${isDark ? "#e5e7eb" : "#1f2937"};
        }
        .compose-quill .ql-editor.ql-blank::before {
          font-style: normal;
          color: ${isDark ? "#4b5563" : "#9ca3af"};
          font-family:
            -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            "Helvetica Neue", Arial, sans-serif !important;
        }
        .compose-quill .ql-editor p,
        .compose-quill .ql-editor li,
        .compose-quill .ql-editor h1,
        .compose-quill .ql-editor h2,
        .compose-quill .ql-editor h3 {
          font-family:
            -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            "Helvetica Neue", Arial, sans-serif !important;
        }
      `}</style>
    </Dialog>
  );
};

export default ComposeEmailDialog;
